import { ATTENDANCE_STATES, ROSTER_SWAP_STATUSES, ROSTER_UNIT_TYPES } from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema, reasonSchema } from './clinical-common.ts';
import { licenceStatusSchema } from './practitioners.ts';

// Roster and attendance (docs/plans/phase3-operations-modules.md §4.2). Clock events are
// UTC instants; shift times are EAT clock times. No payroll.

const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Write HH:MM, like 07:00');

export const rosterShiftSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  startsAt: clockTime,
  endsAt: clockTime,
  active: z.boolean(),
});
export type RosterShiftView = z.infer<typeof rosterShiftSchema>;

export const rosterShiftListSchema = z.object({ items: z.array(rosterShiftSchema) });

export const saveShiftsRequestSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.uuid().nullable(),
        name: z.string().trim().min(1).max(40),
        startsAt: clockTime,
        endsAt: clockTime,
        active: z.boolean(),
      }),
    )
    .max(20),
});
export type SaveShiftsRequest = z.infer<typeof saveShiftsRequestSchema>;

export const rosterUnitSchema = z.object({
  type: z.enum(ROSTER_UNIT_TYPES),
  /** The ward's ID, or the department as written on staff records. */
  key: z.string(),
  name: z.string(),
});
export type RosterUnitView = z.infer<typeof rosterUnitSchema>;

export const rosterUnitsSchema = z.object({ items: z.array(rosterUnitSchema) });

export const rosterQuerySchema = z.object({
  unitType: z.enum(ROSTER_UNIT_TYPES),
  unit: z.string().trim().min(1).max(80),
  from: isoDaySchema,
  to: isoDaySchema,
});

const licenceWarningSchema = licenceStatusSchema;

export const rosterPersonSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  department: z.string().nullable(),
  /** The licence state, for a passive warning (plan R5). `no_profile` for unlicensed staff. */
  licence: licenceWarningSchema,
});
export type RosterPersonView = z.infer<typeof rosterPersonSchema>;

export const rosterEntrySchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  onDate: isoDaySchema,
  shiftId: z.uuid(),
});

export const rosterSchema = z.object({
  unit: rosterUnitSchema,
  from: isoDaySchema,
  to: isoDaySchema,
  shifts: z.array(rosterShiftSchema),
  /** People on the roster in the range, by name. */
  people: z.array(rosterPersonSchema),
  entries: z.array(rosterEntrySchema),
});
export type RosterView = z.infer<typeof rosterSchema>;

/** Active staff who can be put on a roster, those whose staff record names the department first. */
export const rosterCandidatesSchema = z.object({ items: z.array(rosterPersonSchema) });

export const addRosterEntriesRequestSchema = z.object({
  unitType: z.enum(ROSTER_UNIT_TYPES),
  unit: z.string().trim().min(1).max(80),
  userId: z.uuid(),
  shiftId: z.uuid(),
  days: z.array(isoDaySchema).min(1).max(31),
});
export type AddRosterEntriesRequest = z.infer<typeof addRosterEntriesRequestSchema>;

export const removeRosterEntryRequestSchema = z.object({ reason: reasonSchema });

export const requestSwapRequestSchema = z.object({
  fromEntryId: z.uuid(),
  toUserId: z.uuid(),
  /** The colleague's entry to take in return, or null to hand the shift over. */
  toEntryId: z.uuid().nullable(),
});
export type RequestSwapRequest = z.infer<typeof requestSwapRequestSchema>;

export const decideSwapRequestSchema = z.object({ reason: reasonSchema.nullable() });

export const rosterSwapSchema = z.object({
  id: z.uuid(),
  requester: actorSchema,
  toUser: actorSchema,
  from: z.object({ entryId: z.uuid(), onDate: isoDaySchema, shiftName: z.string() }),
  to: z.object({ entryId: z.uuid(), onDate: isoDaySchema, shiftName: z.string() }).nullable(),
  status: z.enum(ROSTER_SWAP_STATUSES),
  requestedAt: z.iso.datetime(),
  decidedBy: actorSchema.nullable(),
  decisionReason: z.string().nullable(),
  /** Whether this reader may approve or reject it: manages rosters and didn't ask. */
  mayDecide: z.boolean(),
});
export type RosterSwapView = z.infer<typeof rosterSwapSchema>;

export const rosterSwapListSchema = z.object({ items: z.array(rosterSwapSchema) });

export const attendanceRecordSchema = z.object({
  id: z.uuid(),
  clockInAt: z.iso.datetime(),
  clockOutAt: z.iso.datetime().nullable(),
});

export const myAttendanceSchema = z.object({
  /** The open clock-in, if any. */
  open: attendanceRecordSchema.nullable(),
  /** The latest records, newest first. */
  recent: z.array(attendanceRecordSchema),
  /** My roster entries today and tomorrow, for the reminder. */
  upcoming: z.array(
    z.object({ onDate: isoDaySchema, shiftName: z.string(), unitName: z.string() }),
  ),
});
export type MyAttendanceView = z.infer<typeof myAttendanceSchema>;

export const attendanceReportSchema = z.object({
  unit: rosterUnitSchema,
  from: isoDaySchema,
  to: isoDaySchema,
  rows: z.array(
    z.object({
      entryId: z.uuid(),
      person: actorSchema,
      onDate: isoDaySchema,
      shiftName: z.string(),
      startsAt: clockTime,
      endsAt: clockTime,
      state: z.enum(ATTENDANCE_STATES),
      clockInAt: z.iso.datetime().nullable(),
      clockOutAt: z.iso.datetime().nullable(),
    }),
  ),
  /** Clock records in the range that match no entry of this unit, for people on its roster. */
  unrostered: z.array(
    z.object({
      id: z.uuid(),
      person: actorSchema,
      clockInAt: z.iso.datetime(),
      clockOutAt: z.iso.datetime().nullable(),
    }),
  ),
  counts: z.record(z.enum(ATTENDANCE_STATES), z.number().int()),
});
export type AttendanceReportView = z.infer<typeof attendanceReportSchema>;
